import { API } from '../Api';
import type { BrandContentFormat, PaginationResponse } from './brand-deal.api';

// Phòng trao đổi theo cặp chiến dịch và creator (spec 2026-10-12 §3.1). `auid` do gateway gắn từ JWT, KHÔNG truyền từ FE.
// - Creator và người đặt: api/v1/brand-threads/*        -> /v1/payment-service/brand-threads/*
// - Admin (chỉ đọc):      api/v1/admin/brand-threads/*  -> /v1/payment-service/admin/brand-threads/*

export type BrandThreadRole = 'Creator' | 'Orderer' | 'Admin';

export interface BrandThreadItem {
  /** Khoá duy nhất ("m:", "e:", "s:"), dùng làm con trỏ trang. */
  key: string;
  type: 'message' | 'event';
  /** ISO có 'Z'. Gửi lại nguyên chuỗi làm con trỏ trang, không đổi qua Date (mất phần lẻ của giây). */
  at: string;
  messageId?: string | null;
  senderRole?: 'Creator' | 'Orderer' | 'System' | null;
  mine: boolean;
  text?: string | null;
  /** Chỉ người gửi và admin nhận true. */
  contactFlag: boolean;
  eventKind?: string | null;
  note?: string | null;
  seq?: number | null;
  max?: number | null;
  actor?: BrandThreadRole | null;
  dealId?: string | null;
  dealKind?: 'Post' | 'Live' | null;
}

export interface BrandThreadDealBrief {
  dealId: string;
  kind: 'Post' | 'Live';
  status: string;
}

export interface BrandThreadRevision {
  dealId: string;
  count: number;
  max: number;
}

export interface BrandThreadTimeline {
  threadId: string;
  campaignId: string;
  campaignTitle: string;
  campaignKind: 'Direct' | 'Creative' | 'Proposal' | 'Booking';
  contentFormat: BrandContentFormat | null;
  myRole: BrandThreadRole;
  canWrite: boolean;
  creatorName: string;
  ordererName: string;
  deals: BrandThreadDealBrief[];
  revision: BrandThreadRevision | null;
  /** "Đã đề nghị chỉnh x/n" — chỉ có ở lời mời đăng bài trực tiếp (spec 2026-10-12 §5.1). */
  directEdit?: BrandThreadRevision | null;
  /** Cũ trước mới sau. */
  items: BrandThreadItem[];
  hasMore: boolean;
}

export interface BrandThreadRef {
  threadId: string;
  campaignKind: 'Direct' | 'Creative' | 'Proposal' | 'Booking';
  myRole: BrandThreadRole;
}

export interface BrandThreadUnread {
  total: number;
  asCreator: number;
  asOrderer: number;
  threadIds: string[];
}

export interface BrandFlaggedMessage {
  messageId: string;
  threadId: string;
  campaignId: string;
  campaignTitle: string;
  creatorId: string;
  creatorName: string;
  senderRole: 'Creator' | 'Orderer';
  text: string;
  createdAt: string;
}

/** Con trỏ trang cũ hơn: `at` và `key` của dòng cũ nhất đang có. */
export interface BrandThreadCursor {
  before: string;
  beforeKey: string;
}

const BASE = '/v1/payment-service/brand-threads';
const ADMIN_BASE = '/v1/payment-service/admin/brand-threads';

export const getBrandThreadTimeline = (threadId: string, cursor?: BrandThreadCursor) =>
  API.get<BrandThreadTimeline>(`${BASE}/${threadId}/timeline`, { params: { ...cursor } }).then((r) => r.data);

export const sendBrandThreadMessage = (threadId: string, text: string) =>
  API.post<BrandThreadItem>(`${BASE}/${threadId}/messages`, { text }).then((r) => r.data);

export const markBrandThreadRead = (threadId: string) =>
  API.post(`${BASE}/${threadId}/read`).then(() => undefined);

export const getBrandThreadUnread = () => API.get<BrandThreadUnread>(`${BASE}/unread-count`).then((r) => r.data);

export const getBrandThreadByDeal = (dealId: string) =>
  API.get<BrandThreadRef>(`${BASE}/by-deal/${dealId}`).then((r) => r.data);

export const adminGetBrandThreadTimeline = (threadId: string, cursor?: BrandThreadCursor) =>
  API.get<BrandThreadTimeline>(`${ADMIN_BASE}/${threadId}/timeline`, { params: { ...cursor } }).then((r) => r.data);

export const adminGetBrandThreadByDeal = (dealId: string) =>
  API.get<BrandThreadRef>(`${ADMIN_BASE}/by-deal/${dealId}`).then((r) => r.data);

export const adminListFlaggedBrandMessages = (page = 1) =>
  API.get<PaginationResponse<BrandFlaggedMessage>>(`${ADMIN_BASE}/flagged`, { params: { page } }).then((r) => r.data);
